import { useEffect, useRef, useState } from 'react';
import type { KitchenBonPreview, KitchenBonScale } from 'shared-models/browser';
import { sessionsService } from '../../../../../services/sessions/sessions.service';
import { toHebrewApiErrorMessage } from '../../../../../services/index/index.service';

export function useKitchenBonSize(tenantId: string, employeeSession: string, value: KitchenBonScale, onClose: () => void) {
  const baseline = useRef(value);
  const [scale, setScale] = useState(value);
  const [image, setImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const saveLock = useRef(false);
  const previews = useRef(new Map<KitchenBonScale, Promise<KitchenBonPreview>>());

  useEffect(() => {
    let active = true;
    setLoading(true);
    setImage(null);
    setError(null);
    let request = previews.current.get(scale);
    if (!request) {
      request = sessionsService.previewKitchenBon(tenantId, scale, employeeSession);
      previews.current.set(scale, request);
    }
    void request.then((preview) => {
      if (active) setImage(preview.imageDataUrl);
    }).catch((failure: unknown) => {
      if (active) setError(toHebrewApiErrorMessage(failure instanceof Error ? failure.message : ''));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [tenantId, employeeSession, scale, retry]);

  const close = () => { if (!saveLock.current) onClose(); };
  const save = async () => {
    if (saveLock.current || loading || !image) return;
    if (scale === baseline.current) { onClose(); return; }
    saveLock.current = true;
    setSaving(true);
    setError(null);
    try {
      await sessionsService.updateKitchenBonScale(tenantId, scale, employeeSession);
      onClose();
    } catch (failure: unknown) {
      setError(toHebrewApiErrorMessage(failure instanceof Error ? failure.message : ''));
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  };
  const retryPreview = () => {
    previews.current.delete(scale);
    setRetry((previous) => previous + 1);
  };
  return { scale, setScale, image, loading, saving, error, save, close, retryPreview };
}